import { useState } from 'react';
import { get, post } from '../lib/api';
import { navigate, storageGet, storageSet, useLoad } from '../lib/hooks';
import { useSession } from '../lib/session';
import { formatCents } from '../../shared/money';
import { Badge, Button, Field, IconButton, Sheet, Tabs, useAction, useToast } from '../ui/kit';
import { Icon, type IconName } from '../ui/icons';
import { TASK_ART } from '../ui/art';
import { PhotoTile } from '../ui/photo';
import { TASK_PHOTOS } from '../../shared/photos';
import { RegisterDeviceSheet } from './Gate';
import { BrandLogo, brandMoment } from '../ui/brand';
import { OpenDrawerSheet } from './Drawer';
import { jobWords, usePlan, type Industry, type PlanFeature } from '../lib/plan';
import { OnboardingCard } from './GettingStarted';
import { zone } from '../../shared/format';

interface Task { key: string; label: string; sub: string; icon: IconName; to?: string; stage?: string; what?: string }

const TASKS: Task[] = [
  { key: 'sale', label: 'SALE', sub: 'Scan or search items', icon: 'bag', to: '/sale' },
  { key: 'repair', label: 'REPAIR', sub: 'Check in a device', icon: 'wrench', to: '/repairs/new' },
  { key: 'buy', label: 'BUY PHONE', sub: 'Website offer + ID', icon: 'phoneCash', to: '/buy' },
  { key: 'trade', label: 'TRADE-IN', sub: 'Credit toward a phone', icon: 'swap', to: '/trade' },
  { key: 'activation', label: 'ACTIVATION', sub: 'New line or port-in', icon: 'sim', to: '/activation' },
  { key: 'billpay', label: 'BILL PAY', sub: 'Carrier + service fee', icon: 'bill', to: '/billpay' },
  { key: 'unlock', label: 'UNLOCK', sub: 'Unlock, reset, software', icon: 'unlock', to: '/repairs/new?service=1' },
  { key: 'order', label: 'CUSTOMER ORDER', sub: 'Order + track a part', icon: 'truck', to: '/orders/new' },
];

const SCOPE_KEY = 'sos_today_scope';

/**
 * The task buttons for this kind of shop and plan: a phone store has the phone tasks (buying
 * phones and special orders when the plan has them); a computer, vacuum or auto shop has selling,
 * checking in (a work order for a vehicle) and special orders. No phone pictures outside phone stores.
 */
function tasksFor(industry: Industry, has: (f: PlanFeature) => boolean): (Task & { photo: boolean })[] {
  if (industry === 'cellphone') {
    return TASKS.filter((t) => (t.key === 'buy' || t.key === 'trade' ? has('buybacks') : t.key === 'order' ? has('special_orders') : true)).map((t) => ({ ...t, photo: true }));
  }
  const w = jobWords(industry);
  const out: (Task & { photo: boolean })[] = [
    { key: 'sale', label: 'SALE', sub: industry === 'auto' ? 'Parts and services' : 'Scan or search items', icon: 'bag', to: '/sale', photo: false },
    { key: 'repair', label: industry === 'auto' ? 'WORK ORDER' : 'REPAIR', sub: w.checkIn, icon: 'wrench', to: '/repairs/new', photo: false },
  ];
  if (has('special_orders')) out.push({ key: 'order', label: 'CUSTOMER ORDER', sub: 'Order + track a part', icon: 'truck', to: '/orders/new', photo: false });
  return out;
}

export function HomeScreen() {
  const { me, can } = useSession();
  const plan = usePlan();
  const tasks = tasksFor(plan.industry, plan.has);
  const words = jobWords(plan.industry);
  // People who work at more than one store pick "This store" or "All stores" (remembered here).
  const [scope, setScope] = useState<'store' | 'all'>(() => (storageGet<string>(SCOPE_KEY, 'store') === 'all' ? 'all' : 'store'));
  const pickScope = (v: 'store' | 'all') => { setScope(v); storageSet(SCOPE_KEY, v); };
  const dash = useLoad(() => get(`/api/dashboard${scope === 'all' ? '?scope=all' : ''}`), [scope]);
  const [soon, setSoon] = useState<Task | null>(null);
  const [register, setRegister] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const d = dash.data;
  // All stores: the tiles add the stores up (and open nothing: the lists show one store).
  const all = d?.scope === 'all';
  const today = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: zone.current }).format(new Date());
  const hour = Number(new Intl.DateTimeFormat('en-US', { hour: 'numeric', hourCycle: 'h23', timeZone: zone.current }).format(new Date()));
  const hello = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  // The logo is painted on once right after someone signs in, not every time they come back here.
  const [paint] = useState(() => {
    const id = me?.employee.id ?? '';
    const first = brandMoment.home !== id;
    brandMoment.home = id;
    return first;
  });
  // Each tile has its own color, so the counter screen reads at a glance.
  const tile = (k: string, v: string, note: string, to: string | undefined, tone: string, icon: IconName) => {
    const I = Icon[icon];
    const body = <><span className="ic"><I /></span><span className="k">{k}</span><span className={`v${v.length >= 11 ? ' xlong' : v.length >= 9 ? ' long' : ''}`}>{v}</span><span className="n">{note}</span></>;
    return to ? <a className={`tile t-${tone}`} href={`#${to}`}>{body}</a> : <div className={`tile t-${tone}`}>{body}</div>;
  };
  return (
    <div className="page">
      <div className="page-head home-hero">
        <div className="hero-text">
          <p className="hero-hi">{hello}{me ? `, ${me.employee.first_name}` : ''}</p>
          <h1>Today</h1>
          <p className="muted">{today} · {me?.location?.name ?? 'No store picked'}</p>
        </div>
        <BrandLogo className="hero-logo" animate={paint} />
      </div>

      {!me?.register ? (
        <div className="banner">
          <Icon.info />
          <div className="grow"><div className="strong">This device is set up for the back office.</div><div className="small">Selling and the cash drawer happen on a store tablet.</div></div>
          {can('registers.manage') ? <Button onClick={() => setRegister(true)}>Make this a store tablet</Button> : null}
        </div>
      ) : d && d.drawer_open === false ? (
        <div className="banner warn">
          <Icon.cash />
          <div className="grow"><div className="strong">The cash drawer is closed.</div><div className="small">Open it to start selling.</div></div>
          <Button variant="primary" onClick={() => setOpenDrawer(true)}>Open drawer</Button>
        </div>
      ) : null}

      {plan.subscriber && can('settings.manage') ? <OnboardingCard /> : null}

      <section aria-label="Start a task">
        <div className="tasks">
          {tasks.map((t) => {
            const Art = TASK_ART[t.key];
            return (
              <button
                key={t.key}
                type="button"
                className={`task ${t.key === 'sale' ? 'primary' : ''}`}
                onClick={() => (t.to ? navigate(t.to) : setSoon(t))}
              >
                <PhotoTile src={t.photo ? TASK_PHOTOS[t.key] : null} alt="" size={76} icon={t.icon} fallback={t.photo || t.key !== 'repair' ? <Art size={64} /> : <span className="task-glyph" aria-hidden="true"><Icon.wrench /></span>} />
                <span>
                  <span className="label" style={{ display: 'block' }}>{t.label}</span>
                  <span className="sub" style={{ display: 'block' }}>{t.sub}</span>
                </span>
                {t.stage ? <span className="soon">Coming soon</span> : null}
              </button>
            );
          })}
        </div>
      </section>

      <section className="stack" aria-label={all ? 'Today at all my stores' : 'Today at this store'}>
        <div className="spread wrap-sm">
          <h2>At a glance</h2>
          {d?.multi ? <Tabs label="Which stores" value={scope} onChange={pickScope} tabs={[{ id: 'store', label: 'This store' }, { id: 'all', label: 'All stores' }]} /> : null}
        </div>
        <div className="tiles">
          {tile("Today's sales", d ? formatCents(d.tiles.today_sales_cents) : '—', d ? `${d.tiles.today_sales_count} sale${d.tiles.today_sales_count === 1 ? '' : 's'}` : '', all ? undefined : '/sales', 'green', 'cash')}
          {tile(`${words.Jobs} waiting`, d?.tiles.repairs_waiting != null ? String(d.tiles.repairs_waiting) : '—', plan.industry === 'auto' ? 'checked in or being worked on' : 'checked in or being fixed', all ? undefined : '/repairs', 'blue', 'wrench')}
          {tile('Waiting on parts', d?.tiles.parts_in_transit != null ? String(d.tiles.parts_in_transit) : '—', `${words.jobs} on hold for a part`, all ? undefined : '/repairs?status=waiting_parts', 'amber', 'clock')}
          {tile('Ready for pickup', d?.tiles.repairs_ready != null ? String(d.tiles.repairs_ready) : '—', plan.home ? 'repairs done, text sent' : `${words.jobs} done`, all ? undefined : '/repairs?status=ready', 'purple', 'check')}
          {tile('Low inventory', d ? String(d.tiles.low_inventory) : '—', 'items at or below minimum', all ? undefined : '/inventory/low', 'red', 'boxes')}
          {plan.has('special_orders') ? tile('Special orders in', d?.tiles.customer_pickup != null ? String(d.tiles.customer_pickup) : '—', d?.tiles.orders_open ? `${d.tiles.orders_open} special order${d.tiles.orders_open === 1 ? '' : 's'} open` : 'orders that arrived', all ? undefined : '/orders?status=arrived', 'teal', 'truck') : null}
          {all && can('drawer.review') ? tile('Drawer counts to review', String(d.tiles.drawer_review), d.tiles.drawer_review ? 'closed with a difference' : 'every count matched', undefined, d.tiles.drawer_review ? 'red' : 'green', 'cash') : null}
          {d?.tiles.visits_today != null && plan.has('visit_booking') ? tile('Store visits today', String(d.tiles.visits_today), d.tiles.visits_waiting ? `${d.tiles.visits_waiting} still coming` : plan.home ? 'booked by phone, text or staff' : 'booked by your team', undefined, 'indigo', 'clock') : null}
        </div>
        {all && d.stores?.length ? <StoreTotals stores={d.stores} drawers={can('drawer.review')} /> : null}
        {d?.alerts?.length ? (
          <div className="alerts">
            {d.alerts.map((a: any) => {
              const body = (
                <>
                  {a.tone === 'critical' ? <Icon.alert /> : a.tone === 'warning' ? <Icon.alert /> : <Icon.info />}
                  <span>{a.label}</span>
                  {a.link ? <span className="go"><Icon.chevronRight /></span> : null}
                </>
              );
              return a.link ? <a key={a.key} className={`alert ${a.tone}`} href={a.link}>{body}</a> : <div key={a.key} className={`alert ${a.tone}`}>{body}</div>;
            })}
          </div>
        ) : d ? (
          <div className="alert good"><Icon.check /><span>{all ? 'Nothing needs attention at any of your stores.' : 'Nothing needs attention right now.'}</span></div>
        ) : null}
      </section>

      {can('repair.create') && me?.location ? <VisitsCard onChange={() => dash.reload()} /> : null}

      {soon ? (
        <Sheet title={soon.label.charAt(0) + soon.label.slice(1).toLowerCase()} onClose={() => setSoon(null)} footer={<Button variant="primary" onClick={() => setSoon(null)}>Got it</Button>}>
          <div className="banner"><Icon.clock /><div><div className="strong">Coming in the {soon.stage}</div><div className="small">This button is in place so the home screen won't change when it arrives.</div></div></div>
          <p>{soon.what}</p>
        </Sheet>
      ) : null}
      {register ? <RegisterDeviceSheet onClose={() => setRegister(false)} /> : null}
      {openDrawer ? <OpenDrawerSheet onClose={() => setOpenDrawer(false)} onDone={() => { setOpenDrawer(false); dash.reload(); }} /> : null}
    </div>
  );
}

export function ComingSoonScreen({ title, stage, children }: { title: string; stage: string; children: React.ReactNode }) {
  return (
    <div className="page">
      <h1>{title}</h1>
      <div className="card stack">
        <div className="banner"><Icon.clock /><div><div className="strong">Coming in the {stage}</div></div></div>
        <div>{children}</div>
      </div>
    </div>
  );
}

/** All stores on Today: each store's numbers, side by side. */
function StoreTotals({ stores, drawers }: { stores: any[]; drawers: boolean }) {
  const n = (v: number) => (v ? String(v) : <span className="muted">0</span>);
  return (
    <div className="store-totals">
      <table className="table">
        <thead>
          <tr>
            <th>Store</th><th>Sales</th><th>Repairs waiting</th><th>On parts</th><th>Ready</th><th>Low stock</th><th>Orders in</th>
            {drawers ? <th>Drawers to review</th> : null}
          </tr>
        </thead>
        <tbody>
          {stores.map((st) => (
            <tr key={st.id}>
              <td className="strong nowrap">{st.name}{st.current ? <span className="tiny muted"> · this store</span> : null}</td>
              <td className="num nowrap">{formatCents(st.tiles.today_sales_cents)} <span className="tiny muted">({st.tiles.today_sales_count})</span></td>
              <td className="num">{n(st.tiles.repairs_waiting)}</td>
              <td className="num">{n(st.tiles.parts_in_transit)}</td>
              <td className="num">{n(st.tiles.repairs_ready)}</td>
              <td className="num">{n(st.tiles.low_inventory)}</td>
              <td className="num">{n(st.tiles.customer_pickup)}</td>
              {drawers ? <td className="num">{st.tiles.drawer_review ? <Badge tone="red">{st.tiles.drawer_review}</Badge> : n(0)}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const SOURCE_LABEL: Record<string, string> = { phone: 'Booked by phone', text: 'Booked by text', staff: 'Booked in store' };

/** The store's time zone (Settings), for the visit times and days. */
function useZone(): string {
  const { me } = useSession();
  return me?.settings?.timezone || zone.current;
}

function clock(ms: number, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone }).format(new Date(ms));
}

/** The store's day for a time: 2026-10-13. */
function dayOf(ms: number, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone }).format(new Date(ms));
}

/** A day plus or minus some days (calendar days, so time changes don't matter). */
function addDays(day: string, n: number): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** "Today · Tue, Oct 13", "Tomorrow · Wed, Oct 14" or "Thu, Oct 15". */
function dayLabel(day: string, today: string): string {
  const [y, m, d] = day.split('-').map(Number);
  const words = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(Date.UTC(y, m - 1, d)));
  const near = day === today ? 'Today' : day === addDays(today, 1) ? 'Tomorrow' : day === addDays(today, -1) ? 'Yesterday' : '';
  return near ? `${near} · ${words}` : words;
}

/**
 * Store visits: booked by the phone agent, the text assistant or staff. One day at a time (the
 * arrows go to other days), or everything coming up in the next 30 days. Visits can be moved or
 * canceled from either.
 */
function VisitsCard({ onChange }: { onChange: () => void }) {
  const tz = useZone();
  const own = usePlan().home;
  const today = dayOf(Date.now(), tz);
  const [view, setView] = useState<'day' | 'upcoming'>('day');
  const [day, setDay] = useState(today);
  const list = useLoad(() => get(`/api/appointments?date=${day}`), [day]);
  const upcoming = useLoad(() => get('/api/appointments/upcoming'), []);
  const [booking, setBooking] = useState(false);
  const [moving, setMoving] = useState<any>(null);
  const [canceling, setCanceling] = useState<any>(null);
  const changed = () => { list.reload(); upcoming.reload(); onChange(); };
  const visits: any[] = list.data?.appointments ?? [];
  const coming: any[] = upcoming.data?.appointments ?? [];
  const groups: { day: string; visits: any[] }[] = [];
  for (const v of coming) {
    const last = groups[groups.length - 1];
    if (last && last.day === v.day) last.visits.push(v);
    else groups.push({ day: v.day, visits: [v] });
  }
  const row = (v: any) => <VisitRow key={v.id} visit={v} past={dayOf(v.starts_at, tz) <= today} onMove={() => setMoving(v)} onCancel={() => setCanceling(v)} onChanged={changed} />;
  return (
    <section className="card stack" aria-label="Booked visits">
      <div className="spread">
        <div>
          <h2>Booked visits</h2>
          <p className="small muted">{own ? 'From the phone agent, texts and staff.' : 'Booked by your team.'}</p>
        </div>
        <Button icon="plus" onClick={() => setBooking(true)}>Book a visit</Button>
      </div>
      <div className="row-wrap visit-tools">
        <Tabs label="Show visits" value={view} onChange={setView} tabs={[{ id: 'day', label: 'By day' }, { id: 'upcoming', label: `Upcoming${coming.length ? ` (${coming.length})` : ''}` }]} />
        {view === 'day' ? (
          <div className="day-nav" role="group" aria-label="Pick a day">
            <IconButton icon="chevronLeft" label="Day before" onClick={() => setDay(addDays(day, -1))} />
            <span className="strong day-name" aria-live="polite">{dayLabel(day, today)}</span>
            <IconButton icon="chevronRight" label="Next day" onClick={() => setDay(addDays(day, 1))} />
            <Button size="sm" disabled={day === today} onClick={() => setDay(today)}>Today</Button>
          </div>
        ) : null}
      </div>
      {view === 'day' ? (
        !visits.length ? (
          <p className="small muted">{list.loading && !list.data ? 'Loading…' : day === today ? 'Nothing booked yet. Walk-ins still come in as usual.' : `Nothing booked ${day < today ? 'that day' : 'yet for that day'}.`}</p>
        ) : (
          <div className="visits">{visits.map(row)}</div>
        )
      ) : !coming.length ? (
        <p className="small muted">{upcoming.loading && !upcoming.data ? 'Loading…' : 'Nothing booked for the next 30 days.'}</p>
      ) : (
        <div className="stack-sm">
          {groups.map((g) => (
            <div key={g.day} className="stack-sm">
              <div className="small strong muted visit-day">{dayLabel(g.day, today)}</div>
              <div className="visits">{g.visits.map(row)}</div>
            </div>
          ))}
        </div>
      )}
      {booking ? <BookVisitSheet day={day >= today ? day : today} onClose={() => setBooking(false)} onDone={() => { setBooking(false); changed(); }} /> : null}
      {moving ? <MoveVisitSheet visit={moving} onClose={() => setMoving(null)} onDone={(to) => { setMoving(null); changed(); if (view === 'day' && to) setDay(to); }} /> : null}
      {canceling ? <CancelVisitSheet visit={canceling} onClose={() => setCanceling(null)} onDone={() => { setCanceling(null); changed(); }} /> : null}
    </section>
  );
}

/** One visit: when, who, what for, and what can be done with it. */
function VisitRow({ visit: v, past, onMove, onCancel, onChanged }: { visit: any; past: boolean; onMove: () => void; onCancel: () => void; onChanged: () => void }) {
  const tz = useZone();
  const { run, busy } = useAction();
  const set = async (status: string, label: string) => {
    const r = await run(() => post(`/api/appointments/${v.id}/status`, { status }), { success: label });
    if (r) onChanged();
  };
  return (
    <div className={`visit ${v.status}`}>
      <span className="when">{clock(v.starts_at, tz)}</span>
      <span className="who">
        <span className="strong">{v.customer_name || v.name}</span>
        <span className="small muted" style={{ display: 'block' }}>
          {[v.device, v.service].filter(Boolean).join(' · ') || 'No details'}{v.phone_display ? ` · ${v.phone_display}` : ''}
        </span>
        {v.notes ? <span className="small" style={{ display: 'block' }}>{v.notes}</span> : null}
        <span className="tiny muted">{SOURCE_LABEL[v.source] ?? v.source}{v.confirmation ? ` · Conf #${v.confirmation}` : ''}</span>
      </span>
      {v.status === 'booked' ? (
        <span className="row-wrap">
          {past ? <Button size="sm" variant="primary" busy={busy} onClick={() => set('arrived', `${v.name} is here.`)}>Here</Button> : null}
          {past ? <Button size="sm" busy={busy} onClick={() => set('no_show', 'Marked as a no-show.')}>No-show</Button> : null}
          <Button size="sm" busy={busy} onClick={onMove}>Move</Button>
          <Button size="sm" busy={busy} onClick={onCancel}>Cancel</Button>
        </span>
      ) : (
        <Badge tone={v.status === 'arrived' ? 'green' : 'amber'}>{v.status === 'arrived' ? 'Here' : 'No-show'}</Badge>
      )}
    </div>
  );
}

/** Move a visit to another day or time (same store); the customer can get a text with the new time. */
function MoveVisitSheet({ visit, onClose, onDone }: { visit: any; onClose: () => void; onDone: (day: string | null) => void }) {
  const tz = useZone();
  const toast = useToast();
  const { run, busy } = useAction();
  const today = dayOf(Date.now(), tz);
  const [date, setDate] = useState(dayOf(visit.starts_at, tz));
  const [time, setTime] = useState(new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(visit.starts_at)));
  // Texting runs on West Coast Wireless's own number: its store only, for now.
  const texting = usePlan().home;
  const [text, setText] = useState(!!visit.phone);
  const save = async () => {
    const r = await run(() => post<any>(`/api/appointments/${visit.id}/move`, { date, time, text: texting && !!visit.phone && text }));
    if (!r) return;
    // The text's outcome too (not sent without their OK to texts).
    toast(`Moved to ${r.when}.${r.text ? ` ${r.text}` : ''}`, r.text?.startsWith('Not sent') ? 'info' : 'good');
    onDone(date);
  };
  return (
    <Sheet title={`Move ${visit.customer_name || visit.name}’s visit`} onClose={onClose} footer={<><Button onClick={onClose}>Keep it</Button><Button variant="primary" busy={busy} disabled={!date || !time} onClick={save}>Move visit</Button></>}>
      <p className="muted">Now: {dayLabel(dayOf(visit.starts_at, tz), today)} at {clock(visit.starts_at, tz)}.</p>
      <div className="form-grid">
        <Field label="New day"><input className="input" type="date" value={date} min={today} max={addDays(today, 30)} onChange={(e) => setDate(e.target.value)} /></Field>
        <Field label="New time"><input className="input" type="time" value={time} onChange={(e) => setTime(e.target.value)} /></Field>
      </div>
      {texting ? <label className="check"><input type="checkbox" checked={!!visit.phone && text} disabled={!visit.phone} onChange={(e) => setText(e.target.checked)} /> Text them the new time{visit.phone ? ' (if they agreed to texts or booked it themselves)' : ' (no phone number)'}</label> : <p className="small muted">No text goes out. Call them if they don’t know yet.</p>}
    </Sheet>
  );
}

function CancelVisitSheet({ visit, onClose, onDone }: { visit: any; onClose: () => void; onDone: () => void }) {
  const tz = useZone();
  const { run, busy } = useAction();
  const cancel = async () => {
    const r = await run(() => post(`/api/appointments/${visit.id}/status`, { status: 'canceled' }), { success: 'Visit canceled.' });
    if (r) onDone();
  };
  return (
    <Sheet title="Cancel this visit?" onClose={onClose} footer={<><Button onClick={onClose}>Keep it</Button><Button variant="danger" busy={busy} onClick={cancel}>Cancel visit</Button></>}>
      <p><span className="strong">{visit.customer_name || visit.name}</span>, {dayLabel(dayOf(visit.starts_at, tz), dayOf(Date.now(), tz))} at {clock(visit.starts_at, tz)}{visit.confirmation ? ` (Conf #${visit.confirmation})` : ''}.</p>
      <p className="small muted">No text goes out. Call or text them if they don’t know yet.</p>
    </Sheet>
  );
}

/** What a visit is for, by kind of shop (placeholders only). */
const VISIT_EXAMPLES: Record<string, [string, string, string]> = {
  cellphone: ['iPhone 13', 'Screen replacement', 'Dropped it, screen is black'],
  computer: ['MacBook Air', 'Battery replacement', 'Shuts off at 30%'],
  auto: ['2019 Mazda3', 'Brake inspection', 'Squeaks when braking'],
  vacuum: ['Dyson V11', 'Cleaning and tune-up', 'Lost suction'],
};

function BookVisitSheet({ day, onClose, onDone }: { day: string; onClose: () => void; onDone: () => void }) {
  const { run, busy } = useAction();
  const plan = usePlan();
  const texting = plan.home;
  const [device, service, notes] = VISIT_EXAMPLES[plan.industry] ?? VISIT_EXAMPLES.cellphone;
  const [f, setF] = useState({ name: '', phone: '', device: '', service: '', notes: '', date: day, time: '', text: true });
  const ok = f.name.trim() && f.date && f.time;
  const save = async () => {
    const r = await run(() => post('/api/appointments', { ...f, text: texting && f.text, phone: f.phone.trim() || null, device: f.device.trim() || null, service: f.service.trim() || null, notes: f.notes.trim() || null }), { success: 'Visit booked.' });
    if (r) onDone();
  };
  const input = (k: 'name' | 'phone' | 'device' | 'service' | 'notes', label: string, props: Record<string, unknown> = {}) => (
    <Field label={label}><input className="input" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} {...props} /></Field>
  );
  return (
    <Sheet title="Book a visit" onClose={onClose} footer={<Button variant="primary" busy={busy} disabled={!ok} onClick={save}>Book it</Button>}>
      <div className="form-grid">
        {input('name', 'Name', { autoFocus: true })}
        {input('phone', 'Phone', { inputMode: 'tel' })}
        {input('device', jobWords(plan.industry).Item, { placeholder: device })}
        {input('service', 'What they need', { placeholder: service })}
        {input('notes', 'Notes for the tech', { placeholder: notes })}
        <Field label="Day"><input className="input" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
        <Field label="Time"><input className="input" type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} /></Field>
      </div>
      {texting ? <label className="check"><input type="checkbox" checked={f.text} disabled={!f.phone.trim()} onChange={(e) => setF({ ...f, text: e.target.checked })} /> Text them a confirmation (if they agreed to texts)</label> : null}
    </Sheet>
  );
}
